import { formatDateNoTime } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React, { useCallback, useState } from "react";
import { handleEvent } from "util/user-events";
import {
    ActionFailureMessage,
    describeForwardingAsk,
    isFailedFor,
    isRunningFor,
    LeadSourceForwardingConfirmation,
    LeadSourceForwardingConfirmationPanel,
    useForwardingAction,
} from "./lead-source-forwarding-confirmation";

export interface LeadSourceApprovedForwardingRequest extends LeadSourceForwardingConfirmation {
    approvedAt: string;
    approvedByName: string;
}

const MARK_NOT_APPROVED = "Mark as not approved";
const UNDO_FAILED_MESSAGE = "The approval could not be undone. Please try again.";

const describeApprover = (request: LeadSourceApprovedForwardingRequest): string =>
    `Approved by ${request.approvedByName} on ${formatDateNoTime(request.approvedAt)}.`;

const describeApproval = (request: LeadSourceApprovedForwardingRequest): string =>
    `Received ${formatDateNoTime(request.receivedAt)}. ${describeApprover(request)}`;

interface LeadSourceApprovedForwardingRowProps {
    request: LeadSourceApprovedForwardingRequest;
    onOpen: (request: LeadSourceApprovedForwardingRequest) => void;
}

const LeadSourceApprovedForwardingRow: React.FC<
    LeadSourceApprovedForwardingRowProps
> = props => {
    const { request, onOpen } = props;
    const open = useCallback(() => onOpen(request), [onOpen, request]);

    return (
        <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2">
            <div className="min-w-0 flex-1 basis-60">
                <p className="break-words text-sm text-gray-900">
                    <span className="font-medium">{request.requesterAddress}</span>{" "}
                    {describeForwardingAsk(request)}
                </p>
                <p className="break-words text-xs text-gray-500">
                    {describeApproval(request)}
                </p>
            </div>
            <div className="flex shrink-0 gap-2">
                <SecondaryCompactButton type="button" onClick={open}>
                    Open email
                </SecondaryCompactButton>
            </div>
        </li>
    );
};

interface LeadSourceApprovedForwardingProps {
    approvedRequests: LeadSourceApprovedForwardingRequest[];
    onMarkNotApproved: (request: LeadSourceApprovedForwardingRequest) => Promise<void>;
}

export const LeadSourceApprovedForwarding: React.FC<
    LeadSourceApprovedForwardingProps
> = props => {
    const { approvedRequests, onMarkNotApproved } = props;
    const [openRequest, setOpenRequest] =
        useState<LeadSourceApprovedForwardingRequest | null>(null);

    const open = useCallback((request: LeadSourceApprovedForwardingRequest) => {
        handleEvent("lead-source-forwarding-confirmation-open");
        setOpenRequest(request);
    }, []);
    const close = useCallback(() => setOpenRequest(null), []);

    const succeeded = useCallback((request: LeadSourceApprovedForwardingRequest) => {
        handleEvent("lead-source-forwarding-mark-not-approved");
        setOpenRequest(current => (current?.id === request.id ? null : current));
    }, []);
    const action = useForwardingAction(onMarkNotApproved, succeeded);
    const undoOpenRequest = useCallback(
        () => (openRequest ? action.run(openRequest) : Promise.resolve()),
        [action, openRequest],
    );

    if (approvedRequests.length === 0) {
        return null;
    }

    return (
        <div className="space-y-2">
            <p className="text-sm font-medium text-gray-500">Approved mail forwarding</p>
            <ul className="divide-y divide-gray-200 rounded-lg ring-1 ring-gray-200">
                {approvedRequests.map(request => (
                    <LeadSourceApprovedForwardingRow
                        key={request.id}
                        request={request}
                        onOpen={open}
                    />
                ))}
            </ul>
            <LeadSourceForwardingConfirmationPanel
                request={openRequest}
                banner={
                    openRequest ? (
                        <InlineBanner type="info">
                            {describeApprover(openRequest)} If the forwarding was never
                            approved, follow the instructions in this email, or mark it as
                            not approved to put the reminder back.
                        </InlineBanner>
                    ) : null
                }
                action={
                    <SecondaryCompactButton
                        type="button"
                        disabled={
                            openRequest
                                ? isRunningFor(action.state, openRequest.id)
                                : false
                        }
                        onClick={undoOpenRequest}>
                        {MARK_NOT_APPROVED}
                    </SecondaryCompactButton>
                }
                failure={
                    openRequest && isFailedFor(action.state, openRequest.id) ? (
                        <ActionFailureMessage message={UNDO_FAILED_MESSAGE} />
                    ) : null
                }
                onDismiss={close}
            />
        </div>
    );
};
